import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaMoneyBillWave, FaShoppingCart } from 'react-icons/fa';
import ReactPixel from 'react-facebook-pixel';
import { v4 as uuidv4 } from 'uuid';
import sha256 from 'crypto-js/sha256';
import ReactGA from "react-ga4";

function CheckoutPage({ setCart, addOrder }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, getAuthHeaders, fetchUserInfo, login } = useAuth();
  const eventId = uuidv4();

  const cartItems = location.state?.cartItems || [];
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    notes: '',
  });
  const nameInputTimer = useRef(null);
  const phoneInputTimer = useRef(null);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [bkashTrxId, setBkashTrxId] = useState('');
  const [bkashPhoneNo, setBkashPhoneNo] = useState('');
  const [paymentModalError, setPaymentModalError] = useState('');
  const [paymentDetailsConfirmed, setPaymentDetailsConfirmed] = useState(false);
  const [mainFormError, setMainFormError] = useState('');

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');

  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [isOrderPlacing, setIsOrderPlacing] = useState(false);

  const [deliveryOption, setDeliveryOption] = useState('');

  const [selectedDivisionName, setSelectedDivisionName] = useState('');
  const [selectedDistrictName, setSelectedDistrictName] = useState('');
  const [selectedUpazilaName, setSelectedUpazilaName] = useState('');
  const [selectedUnionName, setSelectedUnionName] = useState('');

  const [isValid, setIsValid] = useState(false);
  const [message, setMessage] = useState('');
  const [addressMessage, setAddressMessage] = useState('Enter your address or click auto-fill');

  const bKashAgentNumber = '017XXXXXXXX';
  const nagadAgentNumber = '018XXXXXXXX';

  const [divisions, setDivisions] = useState([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState('');
  const [districts, setDistricts] = useState([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [upazilas, setUpazilas] = useState([]);
  const [selectedUpazilaId, setSelectedUpazilaId] = useState('');
  const [unions, setUnions] = useState([]);
  const [selectedUnionId, setSelectedUnionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = 'https://bdapi.vercel.app/api/v.1';

  const [isOrderButtonEnabled, setIsOrderButtonEnabled] = useState(false);
  const [orderId] = useState(uuidv4());
  const clientId = localStorage.getItem('client_id');
  const hasFiredRef = useRef(false);

  function getFbClickId() {
    const match = document.cookie.match(/_fbc=([^;]*)/);
    return match ? match[1] : null;
  }

  function getFbBrowserId() {
    const match = document.cookie.match(/_fbp=([^;]*)/);
    return match ? match[1] : null;
  }

  useEffect(() => {
    if ((selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && !paymentDetailsConfirmed) {
      setShowPaymentModal(true);
    } else {
      setShowPaymentModal(false);
    }

    if (selectedPaymentMethod === 'cashOnDelivery') {
      setPaymentDetailsConfirmed(true);
      setBkashTrxId('');
      setBkashPhoneNo('');
    } else if (selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') {
      if (!paymentDetailsConfirmed) {
        setPaymentDetailsConfirmed(false);
      }
    }
  }, [selectedPaymentMethod, paymentDetailsConfirmed]);

  useEffect(() => {
    const isFormFilled = formData.name.trim() !== '' && formData.phone.trim() !== '' && formData.address.trim() !== '';
    const isPaymentSelected = selectedPaymentMethod !== '';
    const isDeliveryOptionSelected = deliveryOption !== '';

    let isPaymentConfirmed = false;
    if (selectedPaymentMethod === 'cashOnDelivery') {
      isPaymentConfirmed = true;
    } else if (selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') {
      isPaymentConfirmed = paymentDetailsConfirmed;
    } else {
      isPaymentConfirmed = false;
    }

    setIsOrderButtonEnabled(isFormFilled && isPaymentSelected && isDeliveryOptionSelected && isPaymentConfirmed && cartItems.length > 0 && selectedDivisionId.length > 0 && selectedDistrictId.length > 0 && isValid);
  }, [formData, selectedPaymentMethod, paymentDetailsConfirmed, deliveryOption, cartItems, selectedDivisionId, selectedDistrictId, isValid]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'name' || name === 'phone') {
      if (name === 'name') {
        clearTimeout(nameInputTimer.current);
      } else {
        clearTimeout(phoneInputTimer.current);
        const checkValidity = (num) => {
          const digitsOnly = num.replace(/[^0-9+]/g, '');
          if (digitsOnly === '') { setIsValid(false); setMessage('Phone number cannot be empty.'); return; }
          if (digitsOnly.startsWith('+88')) {
            const numberWithoutCountryCode = digitsOnly.slice(1);
            if (numberWithoutCountryCode.length === 13) {
              if (/^\d+$/.test(numberWithoutCountryCode)) { setIsValid(true); setMessage('Valid phone number.'); }
              else { setIsValid(false); setMessage('Use digits only.'); }
            } else { setIsValid(false); setMessage('Provide 13 digits including +88.'); }
          } else {
            if (digitsOnly.length === 11) {
              if (/^\d+$/.test(digitsOnly)) { setIsValid(true); setMessage('Valid phone number.'); }
              else { setIsValid(false); setMessage('Use digits only.'); }
            } else { setIsValid(false); setMessage('Provide 11-digit phone number.'); }
          }
        };
        checkValidity(value);
      }

      const timer = setTimeout(() => { sendRealtimeDataToFlask(name, value); }, 500);
      if (name === 'name') { nameInputTimer.current = timer; }
      else { if (isValid) { phoneInputTimer.current = timer; } }
    } else if (name === 'address') {
      if (formData.address.trim() === '') { setAddressMessage('Enter your address or click auto-fill'); }
      else { setAddressMessage(''); }
    }
  };

  const sendRealtimeDataToFlask = async (fieldName, fieldValue) => {
    const clientId = localStorage.getItem('client_id');
    const today = new Date();
    const dateOnly = today.toDateString();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    try {
      const response = await fetch('https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/api/realtime-input', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          field: fieldName, value: fieldValue, visitPageDate: dateOnly,
          ServerAPICallingDate: `${day}-${month}-${year}`, clientId: clientId,
          cartItems: cartItems.map(item => ({
            productId: item.id, name: item.name, quantity: item.quantity,
            price: item.price, image: item.image, originalPrice: item.originalPrice || item.price,
            selectedSize: item.selectedSize || null, selectedColor: item.selectedColor ? item.selectedColor.name : null,
          })),
        }),
      });
      if (response.ok) { console.log(`Real-time data sent successfully for ${fieldName}: ${fieldValue}`); }
      else { const errorData = await response.json(); console.error(`Failed to send real-time data for ${fieldName}:`, errorData.error); }
    } catch (error) { console.error(`Error sending real-time data for ${fieldName}:`, error); }
  };

  const calculateSubtotal = () => cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;
    const subtotal = calculateSubtotal();
    if (appliedCoupon.discount_type === 'percentage') return (subtotal * appliedCoupon.discount_value) / 100;
    else if (appliedCoupon.discount_type === 'fixed') return appliedCoupon.discount_value;
    return 0;
  };

  const calculateTotalPAW = () => cartItems.reduce((acc, item) => { const pawValue = typeof item.PAW === 'number' ? item.PAW : 0; return acc + (pawValue * item.quantity); }, 0);
  const calculateTotalPCW = () => cartItems.reduce((acc, item) => { const pcwValue = typeof item.PCW === 'number' ? item.PCW : 0; return acc + (pcwValue * item.quantity); }, 0);
  const totalPAW = calculateTotalPAW();
  const totalPCW = calculateTotalPCW();

  const calculateTotalRevenue = () => {
    let totalRevenue = 0;
    cartItems.forEach(item => {
      const price = typeof item.price === 'number' ? item.price : 0;
      const buyingPrice = typeof item.buyingPrice === 'number' ? item.buyingPrice : 0;
      const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
      totalRevenue += (price - buyingPrice) * quantity;
    });
    return totalRevenue;
  };
  const totalRevenue = calculateTotalRevenue();

  const getDeliveryChargeInfo = () => {
    let charge = 0; let display = ''; let isFree = false;
    if (totalPCW === 0) { charge = 0; display = 'Free'; isFree = true; }
    else if (totalPCW > 0 && totalPCW <= 1) {
      if (deliveryOption === 'insideDhaka') { charge = 80; display = '৳80'; }
      else if (deliveryOption === 'outsideDhaka') { charge = 130; display = '৳130'; }
      else { charge = 0; display = 'Select option'; }
    } else if (totalPCW > 1) {
      if (deliveryOption === 'insideDhaka') { charge = Math.round(totalPCW * 80); display = `৳${charge}`; }
      else if (deliveryOption === 'outsideDhaka') { charge = Math.round(totalPCW * 130); display = `৳${charge}`; }
      else { charge = 0; display = 'Select option'; }
    }
    return { chargeForApi: charge, displayCharge: display, isFree: isFree };
  };
  const { chargeForApi, displayCharge, isFree } = getDeliveryChargeInfo();

  const calculateACDC = () => {
    if (totalPAW === 0) return 0;
    else if (totalPAW > 0 && totalPAW <= 0.5) return 50;
    else if (totalPAW > 0.5 && totalPAW <= 1) return 75;
    else return Math.round(totalPAW * 60);
  };
  const acdcValue = calculateACDC();
  let actualTotalRevenue = totalRevenue;
  if (totalPCW === 0) { actualTotalRevenue = totalRevenue - acdcValue; }

  const calculateTotal = () => { const subtotal = calculateSubtotal(); const discount = calculateDiscount(); return Math.max(0, subtotal - discount); };
  const calculateTotalOriginalPrice = () => cartItems.reduce((acc, item) => acc + (item.originalPrice || item.price) * item.quantity, 0);
  const calculateOverallSavings = () => {
    const totalOriginalPrice = calculateTotalOriginalPrice();
    const subtotalCurrentPrice = calculateSubtotal();
    return (totalOriginalPrice - subtotalCurrentPrice) + calculateDiscount();
  };

  const handlePaymentMethodChange = (method) => { setSelectedPaymentMethod(method); setPaymentDetailsConfirmed(false); setMainFormError(''); };
  const handleDeliveryOptionChange = (option) => { setDeliveryOption(option); setMainFormError(''); };

  const handlePaySubmit = () => {
    setPaymentModalError('');
    if (!bkashTrxId.trim()) { setPaymentModalError('TrxID cannot be empty.'); return; }
    if (!/^\d{11}$/.test(bkashPhoneNo)) { setPaymentModalError('Provide valid 11-digit mobile number.'); return; }
    setPaymentDetailsConfirmed(true);
    setShowPaymentModal(false);
  };

  const handleApplyCoupon = async () => {
    setCouponError(''); setCouponSuccess(''); setAppliedCoupon(null);
    if (!couponCode.trim()) { setCouponError('Coupon code cannot be empty.'); return; }
    const clientId = localStorage.getItem('client_id');
    try {
      const response = await fetch(`https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/apply_coupon`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coupon_code: couponCode, cart_total: calculateSubtotal(), client_id: clientId }),
      });
      const data = await response.json();
      if (response.ok) { setAppliedCoupon(data.coupon); setCouponSuccess(data.message); }
      else {
        setCouponError(data.error);
        if (response.status === 401) setCouponError('Your session has expired. Please login again.');
        else if (response.status === 429) setCouponError(data.error);
      }
    } catch (error) { console.error('Error applying coupon:', error); setCouponError('Error applying coupon.'); }
  };

  const updateCouponUsage = async (code) => {
    try {
      const response = await fetch('https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/use_coupon', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coupon_code: code, client_id: clientId }),
      });
      if (response.ok) console.log(`Coupon ${code} usage updated.`);
      else { const errorData = await response.json(); console.error(`Error updating coupon usage: ${errorData.error}`); }
    } catch (error) { console.error('Error updating coupon usage:', error); }
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault(); setMainFormError('');
    if (!isOrderButtonEnabled) { setMainFormError('Please fill all required information.'); return; }
    proceedToOrderSubmission();
  };

  const eventTime = Math.floor(Date.now() / 1000);

  const proceedToOrderSubmission = async () => {
    setIsOrderPlacing(true); setMainFormError('');
    const fbc = getFbClickId(); const fbp = getFbBrowserId();
    const orderSummary = {
      orderId: `${orderId}`, orderDate: new Date().toLocaleDateString(), orderTime: new Date().toLocaleTimeString(),
      eventTime: eventTime, customerInfo: formData, division: selectedDivisionName, district: selectedDistrictName,
      upazila: selectedUpazilaName != null ? selectedUpazilaName : "Not selected",
      cartItems: cartItems.map(item => ({
        productId: item.productId, name: item.name, quantity: item.quantity, price: item.price, image: item.image,
        originalPrice: item.originalPrice || item.price, selectedSize: item.selectedSize || null,
        selectedColor: item.selectedColor ? item.selectedColor.name : null,
        PAW: typeof item.PAW === 'number' ? item.PAW : 0, PCW: typeof item.PCW === 'number' ? item.PCW : 0,
        buyingPrice: typeof item.buyingPrice === 'number' ? item.buyingPrice : 0,
      })),
      subtotalAmount: calculateSubtotal(), discountAmount: calculateDiscount(),
      CustomerDeliveryCharge: chargeForApi, AdjustedCustomerDeliveryCharge: acdcValue,
      totalAmount: calculateTotal(), paymentMethod: selectedPaymentMethod,
      paymentDetails: (selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && paymentDetailsConfirmed
        ? { trxId: bkashTrxId, phoneNo: bkashPhoneNo } : null,
      appliedCouponCode: appliedCoupon ? appliedCoupon.code : null, status: 'Processing', reviewAdded: false,
      clientId: clientId, totalPAW: totalPAW, totalPCW: totalPCW, totalRevenue: totalRevenue,
      ActualTotalRevenue: actualTotalRevenue, deliveryOption: deliveryOption,
      fbc: fbc, fbp: fbp, external_id: clientId,
    };
    try {
      const response = await fetch('https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/place_order', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(orderSummary),
      });
      const data = await response.json();
      if (response.ok) {
        console.log('Order successfully placed:', data.message);
        if (data.access_token && data.user_info) {
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('user_info', JSON.stringify(data.user_info));
          if (typeof login === 'function') { login(data.access_token, data.user_info); }
          localStorage.setItem('just_logged_in', 'true');
          console.log(data.auto_registered ? '✅ Account created!' : '✅ Logged in!');
        }
        if (appliedCoupon) { await updateCouponUsage(appliedCoupon.code); }
        setCart([]);
        navigate('/thank-you', { state: { orderSummary: data.order } });
      } else { setMainFormError(data.error || 'Failed to place order.'); console.error('Failed to place order:', data.error); }
    } catch (error) { console.error('Error placing order:', error); setMainFormError('Network error placing order.'); }
    finally { setIsOrderPlacing(false); }
  };

  const handleAuthPromptGuestCheckout = () => { proceedToOrderSubmission(); };

  useEffect(() => {
    const fetchDivisions = async () => {
      setLoading(true); setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/division`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) setDivisions(data.data);
        else setError("Division data in unexpected format.");
      } catch (err) { console.error("Failed to load divisions:", err); setError(`Error loading divisions: ${err.message}`); }
      finally { setLoading(false); }
    };
    fetchDivisions();
  }, []);

  useEffect(() => {
    const fetchDistricts = async () => {
      if (!selectedDivisionId) { setDistricts([]); setSelectedDistrictId(''); setUpazilas([]); setSelectedUpazilaId(''); setUnions([]); setSelectedUnionId(''); return; }
      setLoading(true); setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/district/${selectedDivisionId}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) { setDistricts(data.data); setSelectedDistrictId(''); setUpazilas([]); setSelectedUpazilaId(''); setUnions([]); setSelectedUnionId(''); }
        else setError("District data in unexpected format.");
      } catch (err) { console.error("Failed to load districts:", err); setError(`Error loading districts: ${err.message}`); }
      finally { setLoading(false); }
    };
    fetchDistricts();
  }, [selectedDivisionId]);

  useEffect(() => {
    const fetchUpazilas = async () => {
      if (!selectedDistrictId) { setUpazilas([]); setSelectedUpazilaId(''); setUnions([]); setSelectedUnionId(''); return; }
      setLoading(true); setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/upazilla/${selectedDistrictId}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) { setUpazilas(data.data); setSelectedUpazilaId(''); setUnions([]); setSelectedUnionId(''); }
        else setError("Upazila data in unexpected format.");
      } catch (err) { console.error("Failed to load upazilas:", err); setError(`Error loading upazilas: ${err.message}`); }
      finally { setLoading(false); }
    };
    fetchUpazilas();
  }, [selectedDistrictId]);

  useEffect(() => {
    const fetchUnions = async () => {
      if (!selectedUpazilaId) { setUnions([]); setSelectedUnionId(''); return; }
      setLoading(true); setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/union/${selectedUpazilaId}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) { setUnions(data.data); setSelectedUnionId(''); }
        else setError("Union data in unexpected format.");
      } catch (err) { console.error("Failed to load unions:", err); setError(`Error loading unions: ${err.message}`); }
      finally { setLoading(false); }
    };
    fetchUnions();
  }, [selectedUpazilaId]);

  const handleDivisionChange = (event) => {
    const selectedId = event.target.value;
    setSelectedDivisionId(selectedId);
    const foundDivision = divisions.find(d => d.id === selectedId);
    if (foundDivision) setSelectedDivisionName(foundDivision.bn_name);
    else setSelectedDivisionName("");
  };

  const handleDistrictChange = (event) => {
    const selectedId = event.target.value;
    setSelectedDistrictId(selectedId);
    const foundDistrict = districts.find(d => d.id === selectedId);
    if (foundDistrict) {
      setSelectedDistrictName(foundDistrict.bn_name);
      if (foundDistrict.bn_name === 'ঢাকা') setDeliveryOption('insideDhaka');
      else if (foundDistrict.bn_name !== '') setDeliveryOption('outsideDhaka');
    } else { setSelectedDistrictName(""); setDeliveryOption(''); }
  };

  const updateQuantity = (productId, newQuantity) => {
    const updatedItems = cartItems.map(item => item.id === productId ? { ...item, quantity: newQuantity } : item).filter(item => item.quantity > 0);
    navigate(location.pathname, { replace: true, state: { cartItems: updatedItems } });
  };

  const removeItem = (productId) => {
    const updatedItems = cartItems.filter(item => item.id !== productId);
    navigate(location.pathname, { replace: true, state: { cartItems: updatedItems } });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const nameVal = document.getElementById("name")?.value || "";
      const phoneVal = document.getElementById("phone")?.value || "";
      const addressVal = document.getElementById("address")?.value || "";
      setFormData(prev => ({ ...prev, name: nameVal, phone: phoneVal, address: addressVal }));
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const grandTotal = calculateTotal() + chargeForApi;

  /* ── RENDER ── */
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f7f8fa', fontFamily: "'Assistant', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

      {/* ── Mobile Sticky Bottom CTA ── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden" style={{ background: '#fff', borderTop: '1px solid #e5e7eb', boxShadow: '0 -4px 20px rgba(0,0,0,0.1)' }}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">Total</span>
            <strong className="text-lg" style={{ color: '#e53e3e' }}>৳{grandTotal}</strong>
          </div>
          <button
            onClick={handleOrderSubmit}
            disabled={!isOrderButtonEnabled || isOrderPlacing}
            className="flex items-center gap-2 px-6 py-3 font-bold text-sm text-white rounded-lg transition-all"
            style={{ backgroundColor: isOrderButtonEnabled && !isOrderPlacing ? '#1a1a2e' : '#9ca3af', minWidth: '160px', justifyContent: 'center' }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            {isOrderPlacing ? 'Placing...' : 'Confirm Order'}
          </button>
        </div>
      </div>

      {/* ── Main ── */}
      <div className="max-w-6xl mx-auto px-4 py-8 pb-24 lg:pb-8">
        <div className="flex gap-6 flex-col lg:flex-row">

          {/* ══ LEFT: Form Column ══ */}
          <div className="flex-1">
            <form onSubmit={handleOrderSubmit} className="space-y-5">

              {/* Contact Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <h2 className="text-base font-bold text-gray-900">Contact Information</h2>
                </div>
                <div className="px-6 py-5 space-y-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      autoComplete="off"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onInput={handleInputChange}
                      required
                      placeholder="01XXXXXXXXX"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all"
                    />
                    {message && (
                      <p className={`text-xs mt-1 ${isValid ? 'text-green-600' : 'text-red-500'}`}>{message}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      autoComplete="off"
                      onChange={handleInputChange}
                      onInput={handleInputChange}
                      required
                      placeholder="Enter your full name"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <h2 className="text-base font-bold text-gray-900">Shipping Address</h2>
                </div>
                <div className="px-6 py-5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Division <span className="text-red-500">*</span></label>
                      <select
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all bg-white"
                        value={selectedDivisionId}
                        onChange={handleDivisionChange}
                        disabled={loading}
                      >
                        <option value="">Select Division</option>
                        {divisions.map(division => (
                          <option key={division.id} value={division.id}>{division.bn_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">District <span className="text-red-500">*</span></label>
                      <select
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all bg-white"
                        value={selectedDistrictId}
                        onChange={handleDistrictChange}
                        disabled={!selectedDivisionId || loading}
                      >
                        <option value="">Select District</option>
                        {districts.map(district => (
                          <option key={district.id} value={district.id}>{district.bn_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Full Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      autoComplete="off"
                      onInput={handleInputChange}
                      rows="3"
                      placeholder="House, Road, Area..."
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all resize-none"
                    />
                    {addressMessage && <p className="text-xs text-gray-400 mt-1">{addressMessage}</p>}
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Note for Delivery
                    </label>
                    <input
                      type="text"
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Special instructions (optional)"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <h2 className="text-base font-bold text-gray-900">Payment Method</h2>
                </div>
                <div className="px-6 py-5 space-y-3">
                  {/* Cash on Delivery */}
                  <label
                    onClick={() => handlePaymentMethodChange('cashOnDelivery')}
                    className="flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all"
                    style={{ borderColor: selectedPaymentMethod === 'cashOnDelivery' ? '#1a1a2e' : '#e5e7eb', backgroundColor: selectedPaymentMethod === 'cashOnDelivery' ? '#f8f9fa' : '#fff' }}
                  >
                    <input type="radio" name="paymentMethod" value="cashOnDelivery" checked={selectedPaymentMethod === 'cashOnDelivery'} onChange={() => handlePaymentMethodChange('cashOnDelivery')} className="w-4 h-4 accent-gray-900" />
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5e9' }}>
                      <svg width="22" height="22" fill="none" stroke="#2e7d32" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">Cash on Delivery</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: '#ef4444' }}>Popular</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">Pay when you receive your order</p>
                    </div>
                  </label>

                  {/* bKash */}
                  <label
                    onClick={() => handlePaymentMethodChange('bkash')}
                    className="flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all"
                    style={{ borderColor: selectedPaymentMethod === 'bkash' ? '#1a1a2e' : '#e5e7eb', backgroundColor: selectedPaymentMethod === 'bkash' ? '#f8f9fa' : '#fff' }}
                  >
                    <input type="radio" name="paymentMethod" value="bkash" checked={selectedPaymentMethod === 'bkash'} onChange={() => handlePaymentMethodChange('bkash')} className="w-4 h-4 accent-gray-900" />
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fce4ec' }}>
                      <img src="https://www.logo.wine/a/logo/BKash/BKash-bKash-Logo.wine.svg" alt="bKash" className="w-7 h-7 object-contain" />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-gray-900">bKash</span>
                      <p className="text-xs text-gray-400 mt-0.5">Pay with bKash mobile wallet</p>
                    </div>
                    <img src="https://www.logo.wine/a/logo/BKash/BKash-bKash-Logo.wine.svg" alt="bKash" className="h-7 w-auto object-contain opacity-60" />
                  </label>

                  {/* Nagad */}
                  <label
                    onClick={() => handlePaymentMethodChange('nagad')}
                    className="flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all"
                    style={{ borderColor: selectedPaymentMethod === 'nagad' ? '#1a1a2e' : '#e5e7eb', backgroundColor: selectedPaymentMethod === 'nagad' ? '#f8f9fa' : '#fff' }}
                  >
                    <input type="radio" name="paymentMethod" value="nagad" checked={selectedPaymentMethod === 'nagad'} onChange={() => handlePaymentMethodChange('nagad')} className="w-4 h-4 accent-gray-900" />
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fff3e0' }}>
                      <img src="https://www.logo.wine/a/logo/Nagad/Nagad-Logo.wine.svg" alt="Nagad" className="w-7 h-7 object-contain" />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-gray-900">Nagad</span>
                      <p className="text-xs text-gray-400 mt-0.5">Pay with Nagad mobile wallet</p>
                    </div>
                    <img src="https://www.logo.wine/a/logo/Nagad/Nagad-Logo.wine.svg" alt="Nagad" className="h-7 w-auto object-contain opacity-60" />
                  </label>

                  {(selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && paymentDetailsConfirmed && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-medium text-green-800 mb-1">Payment Details Submitted ✓</p>
                      <p className="text-xs text-gray-600">TrxID: <strong>{bkashTrxId}</strong> · Phone: <strong>{bkashPhoneNo}</strong></p>
                    </div>
                  )}
                </div>
              </div>

              {mainFormError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-600">{mainFormError}</p>
                </div>
              )}
            </form>
          </div>

          {/* ══ RIGHT: Order Summary ══ */}
          <div className="lg:w-96 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden sticky top-6">

              {/* Summary Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-bold text-gray-900">Order Summary</h3>
                <button onClick={() => navigate(-1)} className="text-sm text-blue-600 hover:underline">Modify</button>
              </div>

              {/* Cart Items */}
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
                {cartItems.map(item => (
                  <div key={item.id} className="flex gap-3 px-4 py-3">
                    <img src={item.image} alt={item.name} loading="lazy" className="w-14 h-14 object-cover rounded-lg flex-shrink-0 border border-gray-100" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">{item.name}</p>
                      {item.selectedSize && <p className="text-[10px] text-gray-400 mt-0.5">Size: {item.selectedSize}</p>}
                      <div className="flex items-center justify-between mt-1.5">
                        <div className="flex items-center gap-1.5 bg-gray-100 rounded-lg px-2 py-0.5">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)} disabled={item.quantity === 1} className="text-gray-500 hover:text-gray-800 disabled:opacity-30 text-sm font-bold w-4">−</button>
                          <span className="text-xs font-semibold w-5 text-center">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="text-gray-500 hover:text-gray-800 text-sm font-bold w-4">+</button>
                        </div>
                        <button onClick={() => removeItem(item.id)} className="text-[10px] text-red-500 hover:text-red-700 font-medium">Remove</button>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {item.originalPrice && item.originalPrice > item.price && (
                        <p className="text-[10px] text-gray-400 line-through">৳{item.originalPrice}</p>
                      )}
                      <p className="text-sm font-bold text-gray-900">৳{item.price * item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Coupon */}
              <div className="px-4 py-3 border-t border-gray-100">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  />
                  <button
                    type="button"
                    onClick={handleApplyCoupon}
                    className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors"
                    style={{ backgroundColor: '#1a1a2e' }}
                  >
                    Apply
                  </button>
                </div>
                {couponError && <p className="text-xs text-red-500 mt-1.5">{couponError}</p>}
                {couponSuccess && <p className="text-xs text-green-600 mt-1.5">{couponSuccess}</p>}
              </div>

              {/* Price Breakdown */}
              <div className="px-4 py-3 border-t border-gray-100 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium text-gray-900">৳{calculateSubtotal()}</span>
                </div>
                {appliedCoupon && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">Coupon ({appliedCoupon.code})</span>
                    <span className="font-medium text-green-600">-৳{calculateDiscount()}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">
                    Shipping {deliveryOption ? `(${deliveryOption === 'insideDhaka' ? 'Dhaka' : 'Outside Dhaka'})` : ''}
                  </span>
                  <span className={`font-medium ${isFree ? 'text-green-600' : 'text-gray-900'}`}>
                    {isFree ? 'Free' : deliveryOption ? displayCharge : '—'}
                  </span>
                </div>
                {calculateOverallSavings() > 0 && (
                  <div className="flex justify-between text-sm bg-green-50 -mx-4 px-4 py-2 rounded">
                    <span className="text-green-700 font-medium">Total Savings</span>
                    <span className="font-bold text-green-700">৳{calculateOverallSavings()}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-100">
                  <span className="text-base font-bold text-gray-900">Total</span>
                  <span className="text-base font-bold" style={{ color: '#e53e3e' }}>৳{grandTotal}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  Delivery within <strong className="text-gray-600 ml-1">2-3 days</strong> after confirmation
                </div>
              </div>

              {/* Confirm Order Button */}
              <div className="px-4 pb-4 pt-2 space-y-3">
                <div className="flex items-start gap-2 text-xs text-gray-400">
                  <input type="checkbox" id="tnc" defaultChecked className="mt-0.5 accent-gray-900" />
                  <label htmlFor="tnc">
                    I agree to the <a href="/terms" className="text-blue-600 hover:underline">Terms</a>,{' '}
                    <a href="/refund-policy" className="text-blue-600 hover:underline">Refund Policy</a> &{' '}
                    <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a>
                  </label>
                </div>

                <button
                  onClick={handleOrderSubmit}
                  disabled={!isOrderButtonEnabled || isOrderPlacing}
                  className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl font-bold text-sm text-white transition-all"
                  style={{ backgroundColor: isOrderButtonEnabled && !isOrderPlacing ? '#1a1a2e' : '#9ca3af', cursor: isOrderButtonEnabled && !isOrderPlacing ? 'pointer' : 'not-allowed' }}
                >
                  <div className="flex items-center gap-2">
                    {isOrderPlacing ? (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    )}
                    <span>{isOrderPlacing ? 'Placing Order...' : 'Confirm Order'}</span>
                  </div>
                  <span className="font-bold">৳{grandTotal}</span>
                </button>

                {/* Trust Badges */}
                <div className="flex items-center justify-around pt-2 border-t border-gray-100">
                  {[
                    { icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', label: 'Secure' },
                    { icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', label: 'Easy Returns' },
                    { icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4', label: 'Fast Delivery' },
                  ].map(({ icon, label }) => (
                    <div key={label} className="flex flex-col items-center gap-1">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={icon} />
                      </svg>
                      <span className="text-[10px] text-gray-400">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Payment Modal (bKash / Nagad) ── */}
      {showPaymentModal && (selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
            <button onClick={() => setShowPaymentModal(false)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-lg font-light transition-colors">×</button>
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              {selectedPaymentMethod === 'bkash' ? 'bKash' : 'Nagad'} Payment
            </h3>
            <p className="text-sm text-gray-500 mb-4">Send payment to the number below and provide details:</p>
            <div className="bg-gray-50 rounded-xl p-3 mb-4 text-center">
              <p className="text-xs text-gray-400 mb-1">Agent Number</p>
              <p className="text-xl font-bold text-gray-900">{selectedPaymentMethod === 'bkash' ? bKashAgentNumber : nagadAgentNumber}</p>
            </div>
            <input
              type="text"
              placeholder="Transaction ID (e.g., 8H7J6K5L)"
              value={bkashTrxId}
              onChange={(e) => setBkashTrxId(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm mb-3 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
            />
            <input
              type="tel"
              placeholder="Your Mobile Number (11 digits)"
              value={bkashPhoneNo}
              onChange={(e) => setBkashPhoneNo(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm mb-3 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
            />
            {paymentModalError && <p className="text-sm text-red-500 mb-3">{paymentModalError}</p>}
            <button onClick={handlePaySubmit} className="w-full py-3 rounded-xl font-bold text-sm text-white transition-colors" style={{ backgroundColor: '#1a1a2e' }}>
              Complete Payment
            </button>
          </div>
        </div>
      )}

      {/* ── Loading Overlay ── */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-40">
          <div className="bg-white rounded-xl px-6 py-4 flex items-center gap-3 shadow-lg">
            <svg className="animate-spin h-5 w-5 text-gray-900" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default CheckoutPage;